import { useState } from 'react';
import { isValidLine } from './Shapes/Line';
import { pointRadius } from './Shapes/Point';
import { isValidPolygon } from './Shapes/Polygon';
import type { DrawingCanvasShapeType, Point } from './types';
import { isNearPoint, isUnmodifiedKey } from './utils';

type UseDrawingProps = {
  isDrawing: boolean;
  drawingShapeType?: DrawingCanvasShapeType.LINE | DrawingCanvasShapeType.POLYGON;
  onFinish: (points: Point[]) => void;
};

/** State and handlers for drawing a new line or polygon one click at a time. */
export const useDrawing = ({ isDrawing, drawingShapeType, onFinish }: UseDrawingProps) => {
  const [draftPoints, setDraftPoints] = useState<Point[]>([]);
  const [cursorPoint, setCursorPoint] = useState<Point | undefined>(undefined);
  const [wasDrawing, setWasDrawing] = useState(isDrawing);
  const [shapeType, setShapeType] = useState(drawingShapeType);

  // Throws away an unfinished drawing when the parent switches modes or shape types
  if (wasDrawing !== isDrawing || shapeType !== drawingShapeType) {
    setWasDrawing(isDrawing);
    setShapeType(drawingShapeType);
    setDraftPoints([]);
    setCursorPoint(undefined);
  }

  const finish = () => {
    setDraftPoints([]);
    onFinish(draftPoints);
  };

  const handlePointerUp = (point: Point) => {
    if (!drawingShapeType) return;

    const lastPoint = draftPoints[draftPoints.length - 1];
    const firstPoint = draftPoints[0];
    const isNearFirst = !!firstPoint && isNearPoint(point, firstPoint, pointRadius / 2);
    const isNearLast = !!lastPoint && isNearPoint(point, lastPoint, pointRadius / 2);
    const draftShape = { type: drawingShapeType, points: draftPoints };
    const isLine = isValidLine(draftShape);
    const isPolygon = isValidPolygon(draftShape);

    if ((isLine && isNearLast) || (isPolygon && (isNearFirst || isNearLast))) {
      finish();
      return;
    }
    // Ignores a click on an existing point before the shape can finish, so points don't stack on top of each other
    if (!isLine && !isPolygon && (isNearFirst || isNearLast)) return;

    setDraftPoints([...draftPoints, point]);
  };

  const handlePointerMove = (point: Point) => setCursorPoint(point);

  const handlePointerLeave = () => setCursorPoint(undefined);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isUnmodifiedKey(event) || (event.key !== 'Enter' && event.key !== 'Escape')) return;
    event.preventDefault();
    finish();
  };

  return { draftPoints, cursorPoint, handlePointerUp, handlePointerMove, handlePointerLeave, handleKeyDown };
};
